import axios from "axios";
import type { AxiosInstance } from "axios";

export interface Terms {
  id: string;
  version: string;
  language: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TermsResponse {
  success: boolean;
  data: Terms;
  requestId?: string;
}

export interface AcceptTermsRequest {
  id: string;
}

export interface AcceptTermsResponse {
  success: boolean;
  data?: any;
  message?: string;
  requestId?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

// Configuração do axios com CORS e credentials
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Interceptor de requisição para garantir que os cookies sejam enviados
axiosInstance.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    if (typeof document !== "undefined" && import.meta.env.DEV) {
      const cookies = document.cookie;
      console.log("[termsApi] Cookies disponíveis:", cookies);
      console.log("[termsApi] URL da requisição:", config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor de resposta para debug
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error("[termsApi] Erro na resposta:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    }
    return Promise.reject(error);
  },
);

export async function getTerms(): Promise<TermsResponse> {
  try {
    const response = await axiosInstance.get<TermsResponse>("/terms");
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao buscar termos: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao buscar termos. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao buscar termos: ${error.message}`);
    }
  }
}

export async function acceptTerms(
  data: AcceptTermsRequest,
): Promise<AcceptTermsResponse> {
  try {
    const response = await axiosInstance.patch<AcceptTermsResponse>(
      "/checklist/terms",
      data,
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao aceitar termos: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao aceitar termos. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao aceitar termos: ${error.message}`);
    }
  }
}
